import { Component, Input, OnInit, OnChanges, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { DashboardsService } from '~/services/modules/stocks/dashboards';
import { AssetDashboardsService } from '~/services/modules/assets/asset-dashboards';

@Component({
    selector: 'app-snapshot-widget',
    templateUrl: './snapshot-widget.component.html',
    styleUrls: ['./snapshot-widget.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SnapshotWidgetComponent implements OnInit, OnChanges {
  @Input() data: any; // Optional pre-formatted snapshot data
  @Input() accountInformation: any[]; // Optional raw account info
  @Input() isAssetDashboard: boolean = false;
  @Input() isAllSites: boolean = false;

  loading: boolean = false;
  startAngle = -135;
  endAngle = 135;

  constructor(private dashboardsService: DashboardsService, private assetDashboardsService: AssetDashboardsService) {}

  ngOnInit(): void {
    // Auto-detect "All Sites" when not explicitly provided by the parent
    if (!this.isAllSites) {
      try {
        const loggedInAccountSites = localStorage.getItem('loggedInAccountSites');
        if (loggedInAccountSites) {
          const sites = JSON.parse(loggedInAccountSites);
          if (sites && sites.length > 0) {
            this.isAllSites = sites[0].siteId === 2;
          }
        }
      } catch { }
    }

    if (this.accountInformation && (Array.isArray(this.accountInformation) ? this.accountInformation.length > 0 : !!this.accountInformation)) {
      this.processAccountInfo(this.accountInformation);
    } else if (!this.data) {
      this.fetchAccountInformation();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['accountInformation'] && this.accountInformation) {
      this.processAccountInfo(this.accountInformation);
    }
  }

  fetchAccountInformation(): void {
    this.loading = true;
    const request$ = this.isAssetDashboard
      ? this.assetDashboardsService.getAssetDashboards()
      : this.dashboardsService.getStockDashboards();

    request$.subscribe({
      next: (response: any) => {
        if (response && response['dashboardAccountInformation']) {
          try {
            const accList = typeof response['dashboardAccountInformation'] === 'string'
              ? JSON.parse(response['dashboardAccountInformation'])
              : response['dashboardAccountInformation'];
            this.processAccountInfo(accList);
          } catch (e) {
            console.error('Error parsing dashboardAccountInformation:', e);
          }
        }
        this.loading = false;
      },
      error: (err) => {
        console.error('Error loading snapshot account information:', err);
        this.loading = false;
      }
    });
  }

  processAccountInfo(acc: any): void {
    if (!acc) return;
    if (Array.isArray(acc)) {
      if (acc.length === 0) return;
      acc = acc[0];
    }
    if (typeof acc === 'string') {
      try {
        acc = JSON.parse(acc);
        if (Array.isArray(acc)) {
          if (acc.length === 0) return;
          acc = acc[0];
        }
      } catch (e) {}
    }
    const availCapVal = acc?.AvailableCapacity_availablevalue !== undefined
      ? acc.AvailableCapacity_availablevalue
      : (acc?.AvailableCapacity_percentagevalue !== undefined
        ? acc.AvailableCapacity_percentagevalue
        : (acc?._AvailableCapacity !== undefined
          ? acc._AvailableCapacity
          : (acc?.AvailableCapacity !== undefined ? acc.AvailableCapacity : 0)));

    const rawSiteName = acc?.SITENAME || acc?.siteName || acc?.SiteName || acc?.Account || acc?.AccountName || 'Site1';
    let availableCapacityTitle = 'Site1 Vacant Locations';
    if (rawSiteName) {
      const cleanName = rawSiteName.toString().trim();
      if (cleanName.toLowerCase().includes('vacant locations')) {
        availableCapacityTitle = cleanName;
      } else {
        availableCapacityTitle = `${cleanName} Vacant Locations`;
      }
    }

    this.data = {
      availableCapacityValue: availCapVal,
      availableCapacityTotal: acc?.AvailableCapacity_totalvalue || 100,
      availableCapacityPercentage: availCapVal,
      availableCapacityCenterText: `${availCapVal}%`,
      availableCapacityTitle: availableCapacityTitle,
      availableTrainingValue: acc?.TrainingHours_availablevalue || acc?.AvailableTraining_RemainingHours || 15,
      availableTrainingTotal: acc?.TrainingHours_totalvalue || acc?.AvailableTraining_TotalHours || 20,
      availableTrainingPercentage: this.calculatePercentage(acc?.TrainingHours_availablevalue || acc?.AvailableTraining_RemainingHours || 15, acc?.TrainingHours_totalvalue || acc?.AvailableTraining_TotalHours || 20),
      availableTrainingCenterText: `${acc?.TrainingHours_availablevalue || acc?.AvailableTraining_RemainingHours || 15}h / ${acc?.TrainingHours_totalvalue || acc?.AvailableTraining_TotalHours || 20}h`,
      connectionSpeedValue: acc?.ConnectionSpeed_value || acc?.ConnectionSpeed_RoundtripTime || 0,
      connectionSpeedPercentage: acc?.ConnectionSpeed_percentagevalue || acc?.ConnectionSpeed_PercentageValue || 0,
      connectionSpeedTotal: acc?.ConnectionSpeed_totalvalue || '',
      customerRecordsValue: acc?.AvailableCustomerRecords_availablevalue !== undefined ? acc.AvailableCustomerRecords_availablevalue : 10000,
      customerRecordsTotal: acc?.AvailableCustomerRecords_totalvalue || 10000,
      customerRecordsPercentage: acc?.AvailableCustomerRecords_percentagevalue || 100,
      customerRecordsCenterText: `${acc?.AvailableCustomerRecords_availablevalue !== undefined ? acc.AvailableCustomerRecords_availablevalue : 10000} / ${acc?.AvailableCustomerRecords_totalvalue || 10000}`,
      loggedInUsersCount: acc?.LoggedInUsers_value || acc?.LoggedInUsers_Count || 1,
      loggedInUsersLicensed: acc?.LoggedInUsers_licensed || acc?.LoggedInUsers_Licensed || 10,
      loggedInUsersPercentage: this.calculatePercentage(acc?.LoggedInUsers_value || acc?.LoggedInUsers_Count || 1, acc?.LoggedInUsers_licensed || acc?.LoggedInUsers_Licensed || 10),
      loggedInUsersCenterText: `${acc?.LoggedInUsers_value || acc?.LoggedInUsers_Count || 1} / ${acc?.LoggedInUsers_licensed || acc?.LoggedInUsers_Licensed || 10}`,
      storageSpaceValue: acc?.AvailableStorageSpace_availablevalue !== undefined && acc?.AvailableStorageSpace_availablevalue !== 0 ? acc.AvailableStorageSpace_availablevalue : (acc?.AvailableStorageSpace_totalvalue || 10),
      storageSpaceTotal: acc?.AvailableStorageSpace_totalvalue || 10,
      storageSpacePercentage: acc?.AvailableStorageSpace_percentagevalue || 100,
      storageSpaceCenterText: `${acc?.AvailableStorageSpace_availablevalue !== undefined && acc?.AvailableStorageSpace_availablevalue !== 0 ? acc.AvailableStorageSpace_availablevalue : (acc?.AvailableStorageSpace_totalvalue || 10)}GB / ${acc?.AvailableStorageSpace_totalvalue || 10}GB`,
      subscriptionPercentage: acc?.SubscriptionEndOfLife_availablevalue || 0,
      subscriptionText: acc?.SubscriptionEndOfLife_availablefullstr || '',
      supportPercentage: acc?.SupportEndOfLife_availablevalue || acc?.SupportContractEndOfLife_availablevalue || 0,
      supportText: acc?.SupportEndOfLife_availablefullstr || acc?.SupportContractEndOfLife_availablefullstr || '',
    };
  }

  private calculatePercentage(available: number, total: number): number {
    if (!total || total <= 0) return 0;
    return Math.min(100, Math.round(((available || 0) / total) * 100));
  }

  getGaugeColors(value: number) {
    if (value < 50) return ['#ff5f56'];
    if (value < 80) return ['#ffbd2e'];
    return ['#27c93f'];
  }

  get subscriptionText(): string {
    if (this.data?.subscriptionText) return this.data.subscriptionText;
    if (!this.data?.subscriptionDaysLeft) return 'N/A';
    const days = this.data.subscriptionDaysLeft;
    const years = Math.floor(days / 365);
    const months = Math.floor((days % 365) / 30);
    if (years > 0) return `${years}y ${months}m left`;
    if (months > 0) return `${months}m left`;
    return `${days}d left`;
  }

  get supportText(): string {
    if (this.data?.supportText) return this.data.supportText;
    if (!this.data?.supportDaysLeft) return 'N/A';
    const days = this.data.supportDaysLeft;
    const years = Math.floor(days / 365);
    const months = Math.floor((days % 365) / 30);
    if (years > 0) return `${years}y ${months}m left`;
    if (months > 0) return `${months}m left`;
    return `${days}d left`;
  }
}

